import { Auth } from '@aws-amplify/auth'
import * as Sentry from '@sentry/react'
import store from '../store'
import { SET_FORM_PENDING } from '../reducers/auth-forms'
import {
    clearRedirectUrl,
    setFormFailureAction,
    setFormSuccessAction,
} from './auth'
import {
    getLoggedUser,
    saveLoggedUserTokens,
    saveUserIntoLocalStorage,
    submitWebViewEventWithUserData,
} from '../reducers/auth'
import { setEcho } from './index'
import { LOGIN_ERROR_TOO_MUCH_INVALID_CREDENTIALS } from '../../consts'
import publicAppsyncClient from '../../components/aws/appsync-client-public'
import { COGNITO_USER_EXISTS } from '../../graphql/queries'

/**
 * Send a request for signup and save response to errors or responseData redux field
 * @param {{name, surname, email, password}} formData
 * @returns {Promise}
 */
export const signup = formData => {
    // Set signup form pending
    store.dispatch({
        type: SET_FORM_PENDING,
        formType: 'signupForm',
    })

    // SignUp to Cognito
    return cognitoSignUp(formData.email, formData.password, formData.name, formData.surname)
    // SignUp to backend
        .then(() => window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/registration?system=${window.APP_CONFIG.REACT_APP_SYSTEM}`, formData)
        // SignIn to Cognito
            .then(() => login(formData))
            .catch(errorResponse => {
                const errorString = JSON.stringify(errorResponse)
                Sentry.captureException(new Error(`Error while signup to API (Cognito OK, API ERROR): ${errorString}`))
                return login(formData)
                    .catch(() => {
                        setFormFailureAction('signupForm', errorResponse)
                        return Promise.reject(errorResponse)
                    })
            }))
        // Cognito threw an error
        .catch(error => {
            setFormFailureAction('signupForm', error)
            return Promise.reject(error)
        })
}

/**
 * Sets login form errors
 * @param {Object} formData
 * @param {Object} cognitoResponse
 * @param {Object} sportnectResponse
 * @returns {Promise<never>}
 */
const setLoginFormError = (formData, cognitoResponse, sportnectResponse) => {
    const cognitoError = cognitoResponse?.reason
    const sportnectError = sportnectResponse?.reason?.data?.error

    return cognitoUserExists(formData.email)
        .then(exists => {
            if (exists && cognitoError?.code === 'UserNotFoundException') {
                cognitoError.code = 'UserFacebookPasswordLoginException'
                setFormFailureAction('loginForm', cognitoError)
                return Promise.reject(cognitoError)
            }

            if (sportnectError === 'wrong_password') {
                setFormFailureAction('loginForm', sportnectError)
                return Promise.reject(sportnectError)
            }

            setFormFailureAction('loginForm', cognitoError)
            return Promise.reject(cognitoError)
        })
}

/**
 * Submit login request
 * @param {{email, password}} formData
 * @param {boolean} canRetryLogin
 * @returns {Promise<any>}
 */
export const login = (formData, canRetryLogin = true) => {
    // Set login form pending
    store.dispatch({
        type: SET_FORM_PENDING,
        formType: 'loginForm',
    })

    return Promise.allSettled([
        cognitoSignIn(formData.email, formData.password),
        sportnectLogin(formData.email, formData.password),
    ]).then(responses => {
        const cognitoResponse = responses?.[0]
        const sportnectResponse = responses?.[1]

        const cognitoOk = cognitoResponse?.status === 'fulfilled'
        const sportnectOk = sportnectResponse?.status === 'fulfilled'

        // Cognito OK - API OK
        if (cognitoOk && sportnectOk) {
            return Promise.allSettled([
                saveTokensOfCurrentlyLoggedCognitoUser(),
                saveAttributesOfCurrentlyLoggedCognitoUser(),
            ])
                .then(() => {
                    const apiTokens = sportnectResponse?.value?.data
                    store.dispatch({
                        type: 'SET_STORE_TOKENS',
                        tokens: apiTokens,
                    })

                    saveLoggedUserTokens(apiTokens)
                    getLoggedUser()
                        .then(user => saveUserIntoLocalStorage(user, 'email'))
                        .then(() => {
                            const redirectUrl = store.getState().authForms.redirectUrl

                            if (redirectUrl) {
                                window.location.href = redirectUrl
                            }

                            sessionStorage.removeItem('facebookLogin.redirectPath')

                            Promise.all([
                                exchangeUserIdentifiers(),
                                setEcho(apiTokens),
                                submitWebViewEventWithUserData(formData) || new Promise(resolve => resolve()),
                                setFormSuccessAction('loginForm', apiTokens),
                                clearRedirectUrl(),
                            ])
                        })
                })
                .catch(errorResponse => setFormFailureAction('loginForm', errorResponse))
        }

        // Too many attempts with failed login
        if (isTooManyAttempts(cognitoResponse, sportnectResponse)) {
            const error = LOGIN_ERROR_TOO_MUCH_INVALID_CREDENTIALS
            setFormFailureAction('loginForm', error)
            return Promise.reject(error)
        }

        // Cognito NOK - API NOK
        if (!cognitoOk && !sportnectOk) {
            return setLoginFormError(formData, cognitoResponse, sportnectResponse)
        }

        // Can retry login
        if (canRetryLogin) {
            // Cognito OK - API NOK
            if (cognitoOk && !sportnectOk) {
                const name = cognitoResponse?.value?.attributes?.name
                return signUpUserFromCognitoToAPI(formData, name)
                    .then(() => login(formData, false))
                    .catch(err => {
                        if (err?.data?.errors?.email?.includes?.('validation.unique')) {
                            return submitNewPasswordToApiForCheckWithCognito(formData.email, formData.password)
                                .then(() => login(formData, false))
                        }
                        setFormFailureAction('loginForm', err)
                        return Promise.reject(err)
                    })
            }

            // Cognito NOK - API OK
            if (!cognitoOk && sportnectOk) {
                const tokensObject = sportnectResponse?.value?.data
                const sportnectAuthorizationToken = `${tokensObject?.token_type} ${tokensObject?.access_token}`
                return signUpUserFromAPIToCognito(formData, sportnectAuthorizationToken).then(() => login(formData, false))
            }
        }

        return new Promise(resolve => resolve())
    })
}

/**
 * Check if user try login too many with bad password
 * @param {Object} cognitoResponse
 * @param {Object} sportnectResponse
 * @returns {boolean}
 */
export const isTooManyAttempts = (cognitoResponse, sportnectResponse) => {
    const isCognitoError = cognitoResponse?.reason?.message === 'Password attempts exceeded'
    const isSportnectError = sportnectResponse?.reason?.data?.error === LOGIN_ERROR_TOO_MUCH_INVALID_CREDENTIALS

    return isCognitoError || isSportnectError
}

/**
 * Submit request for forgotten password
 * @param {{email}} formData
 * @returns {Promise<any>}
 */
export const requestForgottenPassword = formData => {
    // Set forgotten password form pending
    store.dispatch({
        type: SET_FORM_PENDING,
        formType: 'forgottenPasswordForm',
    })

    const email = formData.email

    if (!Auth) {
        return window.swalert('Funkce autorizace není momentálně dostupná')
    }

    return Auth.forgotPassword(email)
}

/**
 * Submit new password to Cognito
 * @param {string} email
 * @param {string} password
 * @param {string} token
 * @return {Promise<any> | Thenable<any> | Promise<T | never> | * | undefined|null}
 */
export const submitNewPasswordToCognito = (email, password, token) => {
    if (!Auth) {
        return window.swalert('Funkce autorizace není momentálně dostupná')
    }

    return Auth.forgotPasswordSubmit(email, token, password)
}

/**
 * Submit new password to API for check with Cognito
 * @param {string} email
 * @param {string} password
 * @return {AxiosPromise<any>}
 */
export const submitNewPasswordToApiForCheckWithCognito = (email, password) => {
    const data = {
        email: email,
        password: password,
        password_confirmation: password,
    }

    return window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/users/me/cognito/password/change`, data)
}

/**
 * Reset password on API
 * @param {Object} postData
 * @returns {AxiosPromise<any>}
 */
export const resetPasswordOnAPI = postData =>
    window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/password/reset`, postData)

/**
 * Shortcut for Cognito sign in
 * @param {string} email
 * @param {string} password
 * @returns {Promise<CognitoUser | any>}
 */
export const cognitoSignIn = (email, password) => {
    if (!Auth) {
        return window.swalert('Funkce autorizace není momentálně dostupná')
    }

    return Auth.signIn({
        username: email,
        password: password,
    })
}

/**
 * Save tokens of logged user into localstorage and store
 * @return {null|*}
 */
export const saveTokensOfCurrentlyLoggedCognitoUser = () => {
    if (!Auth) {
        return window.swalert('Funkce autorizace není momentálně dostupná')
    }

    return Promise.resolve().then(() => Auth.currentSession())
        .then(currentSession => {
            const {
                accessToken: {
                    jwtToken: accessToken,
                },
                idToken: {
                    jwtToken: idToken,
                },
                refreshToken: {
                    token: refreshToken,
                },
            } = currentSession

            const cognitoTokens = {
                accessToken,
                idToken,
                refreshToken,
            }

            localStorage.setItem('cognito.tokens', JSON.stringify(cognitoTokens))

            store.dispatch({
                type: 'SET_COGNITO_STORE_TOKENS',
                tokens: cognitoTokens,
            })
        })
        .catch(() => ({}))
}

/**
 * SignUp user from API to Cognito
 * @param {{email, password}} formData
 * @param {string} sportnectAuthorizationToken
 * @return {Promise<any>}
 */
export const signUpUserFromAPIToCognito = (formData, sportnectAuthorizationToken) => (
    getLoggedUserSimple(sportnectAuthorizationToken).then(response => {
        const {
            name,
            surname,
        } = response.data.data

        const {
            email,
            password,
        } = formData

        return cognitoSignUp(email, password, name, surname)
    })
)

/**
 * SignUp user from Cognito to API
 * @param {{email, password}} formData
 * @param {string} name
 * @return {Promise<any>}
 */
export const signUpUserFromCognitoToAPI = (formData, name) => {
    const names = name.split(' ')
    const firstNames = names.slice(0, names.length - 1).join(' ')
    const lastName = names[names.length - 1]

    const signUpData = {
        ...formData,
        name: firstNames,
        surname: lastName,
    }

    return sportnectSignUp(signUpData)
}

/**
 * Shortcut for Cognito sign up
 * @param {string} email
 * @param {string} password
 * @param {string} name
 * @param {string} surname
 * @returns {Promise<ISignUpResult>}
 */
export const cognitoSignUp = (email, password, name, surname) => {
    if (!Auth) {
        return window.swalert('Funkce autorizace není momentálně dostupná')
    }

    return Auth.signUp({
        username: email,
        attributes: {
            name: `${name} ${surname}`,
            email: email,
        },
        password: password,
    })
}

/**
 * Shortcut for loggedUser
 * @param {string} authorizationToken
 * @returns {AxiosPromise<any>}
 */
export const getLoggedUserSimple = authorizationToken => window.axios.get(`${window.APP_CONFIG.REACT_APP_API_URL}api/logged-user`, {
    headers: {
        Authorization: authorizationToken,
    },
})

/**
 * Shortcut for Sportnect login
 * @param {string} email
 * @param {string} password
 * @returns {AxiosPromise<any>}
 */
export const sportnectLogin = (email, password) => {
    const json = {
        grant_type: 'password',
        client_id: window.APP_CONFIG.REACT_APP_BACKEND_AUTH_ID,
        client_secret: window.APP_CONFIG.REACT_APP_BACKEND_AUTH_SECRET,
        username: email,
        password: password,
        password_confirmation: password,
        scope: '*',
    }

    return window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/oauth/token`, json)
}

/**
 * SignUp user to Sportnect
 * @param {{email, password, name, surname}} formData
 * @return {AxiosPromise<any>}
 */
export const sportnectSignUp = formData => window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/registration?system=${window.APP_CONFIG.REACT_APP_SYSTEM}`, formData)

/**
 * Exchanges user identifiers between api and cognito
 * @returns {Promise}
 */
export const exchangeUserIdentifiers = () => (
    Promise.allSettled([
        saveCognitoSubToApi(),
        saveApiIdToCognito(),
    ])
)

/**
 * Saves cognito user sub to API
 * @return {Promise}
 */
const saveCognitoSubToApi = () => (
    Auth.currentUserInfo()
        .then(response => {
            const {
                email,
                sub,
            } = response?.attributes

            const data = {
                email: email,
                sub: sub,
            }

            return window.axios.post(`${window.APP_CONFIG.REACT_APP_API_URL}api/users/me/cognito/sub`, data)
        })
)

/**
 * Saves user id to cognito
 * @returns {Promise<any>}
 */
const saveApiIdToCognito = () => {
    const loggedUser = store.getState().auth.user

    return Auth.currentUserPoolUser()
        .then(user => (
            Auth.updateUserAttributes(user, {
                'custom:sportnect_id': loggedUser?.id + '',
            })
        ))
}

/**
 * Saves attributes of currently logged in cognito user
 * @returns {null|Promise}
 */
export const saveAttributesOfCurrentlyLoggedCognitoUser = () => {
    if (!Auth) {
        return window.swalert('Funkce autorizace není momentálně dostupná')
    }

    return Promise.resolve().then(() => (
        Auth.currentUserPoolUser()
            .then(user => {
                localStorage.setItem('cognito.user.attributes', JSON.stringify(user.attributes))

                store.dispatch({
                    type: 'SET_COGNITO_USER_ATTRIBUTES',
                    attributes: user.attributes,
                })
            })
            .catch(() => ({}))
    ))
}

/**
 * Checks whether cognito user has email
 * @returns {boolean}
 */
export const cognitoUserHasEmail = () => {
    const attributes = store.getState()?.auth?.cognitoUserAttributes

    if (!attributes) {
        return true
    }

    return !!attributes.email
}

/**
 * Checks whether cognito user exists in the user pool
 * @param {string} email
 * @returns {boolean}
 */
export const cognitoUserExists = email => {
    return publicAppsyncClient.query({
        query: COGNITO_USER_EXISTS,
        variables: {
            email: email,
        },
    }).then(data => data?.data?.cognitoUserExists)
}

/**
 * Updates cognito user attributes
 * @param {Object} attributes
 * @returns {Promise}
 */
export const updateCognitoUserAttributes = attributes =>
    Auth.currentUserPoolUser()
        .then(user => Auth.updateUserAttributes(user, attributes))
